import { Injectable } from '@angular/core';
import { TokenStorageService } from '../token-storage-service/token-storage.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private tokenStorageService: TokenStorageService
  ) { }

  httpOptions = {
    headers: new HttpHeaders().set('Content-Type', 'application/json'),
  };

  httpOptionsWithAuthToken = {
    headers: new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', 'Bearer ' + this.tokenStorageService.getToken()),
  };

  refreshToken(): any {
    const refreshToken = this.tokenStorageService.getRefreshToken();

    let httpOptions = this.httpOptions;
    httpOptions.headers.set('Authorization', 'Bearer ' + refreshToken);

    return this.http.get(
      environment.baseUrl + '/api/v1/auth/refresh-token',
      httpOptions
    );
  }

  getAllUsers(
    search: string = '',
    limit: number = 10,
    offset: number = 0,
    sortParam: string = ''
  ): Observable<any> {
    const queryString = `search=${search}&limit=${limit}&offset=${offset}&sort=${sortParam}`;
    return this.http.get<any>(
      `${environment.baseUrl}/api/v1/user/all?${queryString}`,
      this.httpOptionsWithAuthToken
    );
  }

  getUserDetails(userId: string): any {
    return this.http.get<any>(
      environment.baseUrl + '/api/v1/user/' + userId,
      this.httpOptionsWithAuthToken
    );
  }

  getUserLicensePlates(userId: string): any {
    const url = `${environment.baseUrl}/api/v1/license-plate/${userId}/license-plates`;
    return this.http.get<any>(url, this.httpOptionsWithAuthToken);
  }

  getAllParkingAgents(
    search: string = '',
    limit: number = 10,
    offset: number = 0,
    sortParam: string = ''
  ): Observable<any> {
    const queryString = `search=${search}&limit=${limit}&offset=${offset}&sort=${sortParam}`;
    return this.http.get<any>(
      `${environment.baseUrl}/api/v1/user/parking-agents?${queryString}`,
      this.httpOptionsWithAuthToken
    );
  }

  deleteAgent(agentId: string) {
    return this.http.delete<any>(
      environment.baseUrl + '/api/v1/user/' + agentId,
      this.httpOptionsWithAuthToken
    );
  }

  getAllParkingRequests(
    search: string = '',
    type: string,
    limit: number = 10,
    offset: number = 0,
    sortParam: string = ''
  ): any {
    const queryString = `search=${search}&type=${type}&limit=${limit}&offset=${offset}&sort=${sortParam}`;
    return this.http.get<any>(
      `${environment.baseUrl}/api/v1/subscription?${queryString}`,
      this.httpOptionsWithAuthToken
    );
  }

  getParkingSubscriptionRequestCount() {
    return this.http.get<any>(
      environment.baseUrl + '/api/v1/subscription/count',
      this.httpOptionsWithAuthToken
    );
  }

  getUserRequestById(requestId: string) {
    return this.http.get<any>(
      environment.baseUrl + '/api/v1/subscription/' + requestId,
      this.httpOptionsWithAuthToken
    );
  }

  putSubscriptionRequestById(
    requestId: string,
    status: string,
    rejectionReason: string
  ) {
    const putData = {
      status: status,
      rejectionReason: rejectionReason,
    };
    return this.http.put<any>(
      environment.baseUrl +
      '/api/v1/subscription/' +
      requestId +
      '/change-status',
      putData,
      this.httpOptionsWithAuthToken
    );
  }

  getParkingSubscriptionRequestDocuments(documentId: string) {
    return this.http.get<any>(
      environment.baseUrl + '/api/v1/subscription-document/' + documentId,
      this.httpOptionsWithAuthToken
    );
  }

  putSubscriptionReqDocById(documentId: string, status: string) {
    const putData = {
      status: status,
    };
    return this.http.put<any>(
      environment.baseUrl +
      '/api/v1/subscription-document/' +
      documentId +
      '/change-status',
      putData,
      this.httpOptionsWithAuthToken
    );
  }

  getAllTickets(
    search: string = '',
    limit: number = 20,
    offset: number = 0,
    startDate: any = '',
    endDate: any = '',
    sortParam: string = ''
  ): Observable<any> {
    let queryParams = `search=${search}&limit=${limit}&offset=${offset}&sort=${sortParam}`;

    if (startDate !== '') {
      queryParams += `&startDate=${startDate}`;
    }

    if (endDate !== '') {
      queryParams += `&endDate=${endDate}`;
    }

    return this.http.get<any>(
      `${environment.baseUrl}/api/v1/ticket/all?${queryParams}`,
      this.httpOptionsWithAuthToken
    );
  }

  getAllSubscriptions(
    search: string = '',
    limit: number = 10,
    offset: number = 0,
    statuses: string[] = [],
    startDate: any = '',
    endDate: any = '',
    sortParam: string = '',
    offstreetIdentificationType: string = ''
  ): Observable<any> {
    let queryParams = `search=${search}&limit=${limit}&offset=${offset}&sort=${sortParam}`;

    if (statuses.length > 0) {
      queryParams += `&statuses=${statuses.join(',')}`;
    }

    if (startDate !== '') {
      queryParams += `&startDate=${startDate}`;
    }

    if (endDate !== '') {
      queryParams += `&endDate=${endDate}`;
    }

    if (offstreetIdentificationType !== '') {
      queryParams += `&offstreetIdentificationType=${offstreetIdentificationType}`;
    }

    return this.http.get<any>(
      `${environment.baseUrl}/api/v2/subscription/all?${queryParams}`,
      this.httpOptionsWithAuthToken
    );
  }

  getTicketCountForUser(userId: string = ''): Observable<any> {
    return this.http.get<any>(
      `${environment.baseUrl}/api/v1/ticket/userTicketCount/${userId}`,
      this.httpOptionsWithAuthToken
    );
  }

  getSubscriptionsForUser(userId: string = ''): Observable<any> {
    return this.http.get<any>(
      `${environment.baseUrl}/api/v2/subscription/by-user/${userId}`,
      this.httpOptionsWithAuthToken
    );
  }

  postUser(
    role: string,
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ): Observable<any> {
    const user = {
      role: role,
      firstName: firstName,
      lastName: lastName,
      email: email,
      password: password,
    };

    return this.http.post(
      `${environment.baseUrl}/api/v1/user`,
      user,
      this.httpOptionsWithAuthToken
    );
  }
}
